import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMicrophone,
  faPaperPlane,
  faTrash
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import type { RecorderFormat } from "@/features/chat/shared/utils/audio.utils";
import {
  createIdempotencyKey,
  MAX_VOICE_MESSAGE_DURATION_MS,
  MAX_VOICE_MESSAGE_SIZE_BYTES,
  selectRecorderFormat
} from "@/features/chat/shared/utils/audio.utils";
import * as styles from "./voice-message-recorder.css";

const CANCEL_DISTANCE_PX = 96;
const MICROPHONE_GRANTED_STORAGE_KEY = "voice-message-microphone-granted";

type PermissionSheet = "prompt" | "denied";

type Preview = {
  audio: Blob;
  durationMs: number;
  format: RecorderFormat;
  idempotencyKey: string;
  mediaId?: string;
  url: string;
};

export interface VoiceMessageRecorderProps {
  disabled?: boolean;
  uploadVoiceMessage: (params: {
    audio: Blob;
    durationMs: number;
    format: RecorderFormat;
    signal: AbortSignal;
  }) => Promise<string>;
  sendVoiceMessage: (params: {
    mediaId: string;
    idempotencyKey: string;
    signal: AbortSignal;
  }) => Promise<void>;
  onActiveChange?: (active: boolean) => void;
}

const hasRememberedMicrophoneGrant = () => {
  try {
    return localStorage.getItem(MICROPHONE_GRANTED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const rememberMicrophoneGrant = (granted: boolean) => {
  try {
    if (granted) {
      localStorage.setItem(MICROPHONE_GRANTED_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(MICROPHONE_GRANTED_STORAGE_KEY);
    }
  } catch {
    return;
  }
};

const stopTracks = (stream: MediaStream | null) =>
  stream?.getTracks().forEach((track) => track.stop());

const audioConstraints: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  }
};

const getMicrophonePermission = async (): Promise<PermissionState | null> => {
  if (!navigator.permissions?.query) return null;

  try {
    const status = await navigator.permissions.query({
      name: "microphone"
    } as PermissionDescriptor);
    return status.state;
  } catch {
    return null;
  }
};

const formatDuration = (durationMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(
    2,
    "0"
  )}`;
};

export const VoiceMessageRecorder = ({
  disabled = false,
  uploadVoiceMessage,
  sendVoiceMessage,
  onActiveChange
}: VoiceMessageRecorderProps) => {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [recordingLevels, setRecordingLevels] = useState<number[]>([]);
  const [recordedDurationMs, setRecordedDurationMs] = useState(0);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [permissionSheet, setPermissionSheet] =
    useState<PermissionSheet | null>(null);
  const [unsupportedError, setUnsupportedError] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const startXRef = useRef(0);
  const pointerHeldRef = useRef(false);
  const cancelOnStopRef = useRef(false);
  const operationControllerRef = useRef<AbortController | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const maximumDurationRef = useRef<number | null>(null);
  const analyserFrameRef = useRef<number | null>(null);
  const analyserContextRef = useRef<AudioContext | null>(null);
  const previewRef = useRef<Preview | null>(null);
  const mountedRef = useRef(true);
  const microphoneGrantedRef = useRef(hasRememberedMicrophoneGrant());

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  useEffect(() => {
    onActiveChange?.(isRecording || preview !== null);
  }, [isRecording, onActiveChange, preview]);

  const clearTimers = useCallback(() => {
    if (durationIntervalRef.current !== null) {
      window.clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (maximumDurationRef.current !== null) {
      window.clearTimeout(maximumDurationRef.current);
      maximumDurationRef.current = null;
    }
  }, []);

  const stopLiveWaveform = useCallback((reset = true) => {
    if (analyserFrameRef.current !== null) {
      window.cancelAnimationFrame(analyserFrameRef.current);
      analyserFrameRef.current = null;
    }
    const context = analyserContextRef.current;
    analyserContextRef.current = null;
    if (context && context.state !== "closed") void context.close();
    if (reset) setRecordingLevels([]);
  }, []);

  const startLiveWaveform = useCallback(
    (stream: MediaStream) => {
      const Context =
        window.AudioContext ??
        (
          window as typeof window & {
            webkitAudioContext?: typeof AudioContext;
          }
        ).webkitAudioContext;
      if (!Context) return;

      stopLiveWaveform();
      const context = new Context();
      const analyser = context.createAnalyser();
      analyser.fftSize = 64;
      context.createMediaStreamSource(stream).connect(analyser);
      analyserContextRef.current = context;
      const samples = new Uint8Array(analyser.frequencyBinCount);

      const draw = () => {
        analyser.getByteFrequencyData(samples);
        setRecordingLevels(
          Array.from({ length: 12 }, (_, index) =>
            Math.max(0.12, (samples[index * 2] ?? 0) / 255)
          )
        );
        analyserFrameRef.current = window.requestAnimationFrame(draw);
      };
      draw();
    },
    [stopLiveWaveform]
  );

  const discardPreview = useCallback(() => {
    operationControllerRef.current?.abort();
    operationControllerRef.current = null;
    setPreview((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
    setSendError(false);
    setIsSending(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pointerHeldRef.current = false;
      clearTimers();
      stopLiveWaveform(false);
      operationControllerRef.current?.abort();
      if (recorderRef.current?.state === "recording") {
        cancelOnStopRef.current = true;
        recorderRef.current.stop();
      }
      stopTracks(streamRef.current);
      if (previewRef.current) URL.revokeObjectURL(previewRef.current.url);
    };
  }, [clearTimers, stopLiveWaveform]);

  const stopRecording = useCallback(
    (cancelled: boolean) => {
      cancelOnStopRef.current = cancelled;
      clearTimers();
      stopLiveWaveform();
      const recorder = recorderRef.current;
      if (recorder?.state === "recording") recorder.stop();
      setIsRecording(false);
      setIsCancelling(false);
    },
    [clearTimers, stopLiveWaveform]
  );

  const startRecording = useCallback(async () => {
    discardPreview();
    setPermissionSheet(null);
    setUnsupportedError(false);
    const format = selectRecorderFormat((mimeType) =>
      MediaRecorder.isTypeSupported(mimeType)
    );
    if (!format) {
      setUnsupportedError(true);
      return;
    }

    // Some browsers keep reporting "prompt" even after the user granted access,
    // so a grant observed in this session wins over the query. Without this the
    // sheet would reopen on every press and recording could never start.
    const permission = microphoneGrantedRef.current
      ? "granted"
      : await getMicrophonePermission();
    if (!mountedRef.current || !pointerHeldRef.current) return;
    if (permission === "prompt" || permission === "denied") {
      pointerHeldRef.current = false;
      setPermissionSheet(permission);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        audioConstraints
      );
      microphoneGrantedRef.current = true;
      rememberMicrophoneGrant(true);
      if (!mountedRef.current || !pointerHeldRef.current) {
        stopTracks(stream);
        return;
      }

      const recorder = new MediaRecorder(stream, {
        mimeType: format.recorderMimeType
      });
      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      startLiveWaveform(stream);
      cancelOnStopRef.current = false;
      startedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        clearTimers();
        stopTracks(streamRef.current);
        streamRef.current = null;
        recorderRef.current = null;
        if (cancelOnStopRef.current || !mountedRef.current) {
          chunksRef.current = [];
          if (mountedRef.current) setRecordedDurationMs(0);
          return;
        }

        const durationMs = Math.max(1, Date.now() - startedAtRef.current);
        const audio = new Blob(chunksRef.current, {
          type: format.uploadMimeType
        });
        chunksRef.current = [];
        if (audio.size === 0 || audio.size > MAX_VOICE_MESSAGE_SIZE_BYTES) {
          setSendError(true);
          return;
        }
        const url = URL.createObjectURL(audio);
        setPreview({
          audio,
          durationMs,
          format,
          idempotencyKey: createIdempotencyKey(),
          url
        });
        setRecordedDurationMs(durationMs);
      };

      recorder.start(250);
      setRecordedDurationMs(0);
      setIsRecording(true);
      durationIntervalRef.current = window.setInterval(() => {
        setRecordedDurationMs(Date.now() - startedAtRef.current);
      }, 100);
      maximumDurationRef.current = window.setTimeout(() => {
        pointerHeldRef.current = false;
        stopRecording(false);
      }, MAX_VOICE_MESSAGE_DURATION_MS);
    } catch (error) {
      stopTracks(streamRef.current);
      streamRef.current = null;
      if (!mountedRef.current) return;
      if (
        error instanceof DOMException &&
        (error.name === "NotAllowedError" || error.name === "SecurityError")
      ) {
        microphoneGrantedRef.current = false;
        rememberMicrophoneGrant(false);
        setPermissionSheet("denied");
      } else {
        setUnsupportedError(true);
      }
    }
  }, [clearTimers, discardPreview, startLiveWaveform, stopRecording]);

  const requestMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        audioConstraints
      );
      stopTracks(stream);
      microphoneGrantedRef.current = true;
      rememberMicrophoneGrant(true);
      if (mountedRef.current) setPermissionSheet(null);
    } catch (error) {
      if (!mountedRef.current) return;
      if (
        error instanceof DOMException &&
        (error.name === "NotAllowedError" || error.name === "SecurityError")
      ) {
        microphoneGrantedRef.current = false;
        rememberMicrophoneGrant(false);
        setPermissionSheet("denied");
      } else {
        setPermissionSheet(null);
        setUnsupportedError(true);
      }
    }
  }, []);

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (disabled || isSending || (event.button ?? 0) !== 0) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      pointerHeldRef.current = true;
      startXRef.current = event.clientX;
      setIsCancelling(false);
      void startRecording();
    },
    [disabled, isSending, startRecording]
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (!pointerHeldRef.current || !isRecording) return;
      setIsCancelling(startXRef.current - event.clientX >= CANCEL_DISTANCE_PX);
    },
    [isRecording]
  );

  const handlePointerEnd = useCallback(() => {
    pointerHeldRef.current = false;
    if (isRecording) stopRecording(isCancelling);
  }, [isCancelling, isRecording, stopRecording]);

  const handleSend = useCallback(async () => {
    if (!preview || isSending) return;
    operationControllerRef.current?.abort();
    const controller = new AbortController();
    operationControllerRef.current = controller;
    setIsSending(true);
    setSendError(false);

    try {
      let mediaId = preview.mediaId;
      if (!mediaId) {
        mediaId = await uploadVoiceMessage({
          audio: preview.audio,
          durationMs: preview.durationMs,
          format: preview.format,
          signal: controller.signal
        });
        setPreview((current) => (current ? { ...current, mediaId } : current));
      }
      await sendVoiceMessage({
        mediaId,
        idempotencyKey: preview.idempotencyKey,
        signal: controller.signal
      });
      if (!controller.signal.aborted) discardPreview();
    } catch (error) {
      if (!controller.signal.aborted) setSendError(true);
    } finally {
      if (!controller.signal.aborted) setIsSending(false);
    }
  }, [
    discardPreview,
    isSending,
    preview,
    sendVoiceMessage,
    uploadVoiceMessage
  ]);

  if (preview) {
    return (
      <div className={styles.preview} data-testid="voice-message-preview">
        <button
          type="button"
          className={styles.actionButton}
          onClick={discardPreview}
          disabled={isSending}
          aria-label={t("creators_chat_voice_delete", {
            defaultValue: "Delete voice message"
          })}
          data-testid="voice-message-delete"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
        <audio
          className={styles.audio}
          src={preview.url}
          controls
          preload="metadata"
          data-testid="voice-message-audio"
        />
        <span className={styles.duration}>
          {formatDuration(recordedDurationMs)}
        </span>
        <button
          type="button"
          className={styles.sendButton}
          onClick={() => void handleSend()}
          disabled={isSending}
          aria-busy={isSending}
          aria-label={
            isSending
              ? t("creators_chat_voice_sending", {
                  defaultValue: "Sending voice message"
                })
              : t("creators_chat_voice_send", {
                  defaultValue: "Send voice message"
                })
          }
          data-testid="voice-message-send"
        >
          <FontAwesomeIcon icon={faPaperPlane} />
          {isSending && (
            <span
              className={styles.sendLoader}
              aria-hidden="true"
              data-testid="voice-message-send-loader"
            />
          )}
        </button>
        {sendError && (
          <span role="alert" className={styles.error}>
            {t("creators_chat_voice_send_error", {
              defaultValue: "Could not send. Tap send to retry."
            })}
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className={
          isRecording
            ? styles.recordingContainerActive
            : styles.recordingContainer
        }
      >
        {isRecording && (
          <div
            className={
              isCancelling ? styles.cancelActive : styles.recordingHint
            }
            aria-live="polite"
            data-testid="voice-message-recording"
          >
            <FontAwesomeIcon icon={faTrash} />
            <span className={styles.recordingDot} aria-hidden="true" />
            <span className={styles.recordingDuration}>
              {formatDuration(recordedDurationMs)}
            </span>
            <span className={styles.liveWaveform} aria-hidden="true">
              {(recordingLevels.length
                ? recordingLevels
                : Array.from({ length: 12 }, () => 0.12)
              ).map((level, index) => (
                <span
                  key={index}
                  className={styles.liveWaveformBar}
                  style={{ height: `${Math.round(level * 100)}%` }}
                  data-testid="voice-message-live-bar"
                />
              ))}
            </span>
            <span className={styles.recordingInstruction}>
              {isCancelling
                ? t("creators_chat_voice_release_cancel", {
                    defaultValue: "Release to cancel"
                  })
                : t("creators_chat_voice_slide_cancel", {
                    defaultValue: "Slide left to delete"
                  })}
            </span>
          </div>
        )}
        <button
          type="button"
          className={
            isRecording ? styles.recordButtonActive : styles.recordButton
          }
          disabled={disabled || isSending}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onContextMenu={(event) => event.preventDefault()}
          aria-label={t("creators_chat_voice_hold", {
            defaultValue: "Hold to record a voice message"
          })}
          data-testid="voice-message-record"
        >
          <FontAwesomeIcon icon={faMicrophone} />
        </button>
      </div>

      {(permissionSheet || unsupportedError) && (
        <div className={styles.sheetBackdrop} data-testid="voice-message-sheet">
          <section
            className={styles.permissionSheet}
            role="dialog"
            aria-modal="true"
            aria-labelledby="voice-message-sheet-title"
          >
            <h2 id="voice-message-sheet-title" className={styles.sheetTitle}>
              {permissionSheet === "prompt"
                ? t("creators_chat_voice_permission_prompt_title", {
                    defaultValue: "Allow microphone access"
                  })
                : permissionSheet === "denied"
                ? t("creators_chat_voice_permission_blocked_title", {
                    defaultValue: "Microphone access is blocked"
                  })
                : t("creators_chat_voice_unsupported_title", {
                    defaultValue: "Voice messages are unavailable"
                  })}
            </h2>
            <p className={styles.sheetText}>
              {permissionSheet === "prompt"
                ? t("creators_chat_voice_permission_prompt_description", {
                    defaultValue:
                      "Allow microphone access to record a voice message."
                  })
                : permissionSheet === "denied"
                ? t("creators_chat_voice_permission_blocked_description", {
                    defaultValue:
                      "Open your browser settings, allow microphone access for MYM, then try again."
                  })
                : t("creators_chat_voice_unsupported_description", {
                    defaultValue:
                      "Use an up-to-date version of Safari or Chrome to record audio."
                  })}
            </p>
            <button
              type="button"
              className={styles.sheetButton}
              onClick={() => {
                if (permissionSheet === "prompt") {
                  void requestMicrophonePermission();
                  return;
                }
                setPermissionSheet(null);
                setUnsupportedError(false);
              }}
            >
              {permissionSheet === "prompt"
                ? t("creators_chat_voice_permission_allow", {
                    defaultValue: "Allow microphone"
                  })
                : permissionSheet === "denied"
                ? t("creators_chat_voice_permission_settings", {
                    defaultValue: "Open browser settings"
                  })
                : t("creators_chat_voice_sheet_close", {
                    defaultValue: "Close"
                  })}
            </button>
          </section>
        </div>
      )}
    </>
  );
};
